import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAPI } from '../Hooks/useAPIs';
import { format } from 'date-fns';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip
} from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import {
    Refresh,
    Search,
    Clear,
    ExpandMore,
    ExpandLess,
    ContentCopy
} from '@mui/icons-material';

const PNPComponent = ({ fileLocation, filename }) => {
    const title = "Vehicle Data";
    const title2 = "ECU Info";
    const [rawData, setRawData] = useState(null);

    const displayFilename = useMemo(() => {
        if (!filename) return 'PNP File Preview';
        const lastSlash = Math.max(filename.lastIndexOf('/'), filename.lastIndexOf('\\'));
        return lastSlash >= 0 ? filename.substring(lastSlash + 1) : filename;
    }, [filename]);
    const [rowSelection, setRowSelection] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('table');
    const [columnFilters, setColumnFilters] = useState([]);
    const [expandedCells, setExpandedCells] = useState({});
    const [copiedCell, setCopiedCell] = useState(null);
    const [pnpData, setPnpData] = useState([]);
    const [vehData, setVehData] = useState(null);
    const [ecuData, setEcuData] = useState([]);
    const [pnpDate, setPNPDate] = useState('N/A');
    const [selectedEcuData, setSelectedEcuData] = useState(null);

    const { apiCall } = useAPI();
    const getPNPFromStorage = async () => {
        if (!fileLocation) return;

        setLoading(true);
        setError(null);

        try {
            const response = await apiCall('get', 'api/v1/part_number_pulls/by_filename', {
                params: { file_location: fileLocation },
            });
            const jsonResponse = response.data;
            setRawData(jsonResponse);
            const convertedData = convertPNPDataToTable(jsonResponse);
            setPnpData(convertedData);
            const ecuInfos = processVehicleData(jsonResponse || {});
            setEcuData(ecuInfos);
        } catch (err) {
            setError(`Failed to load PNP file: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const toggleCellExpansion = useCallback((cellId) => {
        setExpandedCells(prev => ({
            ...prev,
            [cellId]: !prev[cellId]
        }));
    }, []);

    const copyToClipboard = useCallback(async (content, cellId) => {
        try {
            await navigator.clipboard.writeText(content);
            setCopiedCell(cellId);
            setTimeout(() => setCopiedCell(null), 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    }, []);

    const formatDateSafely = useCallback((dateValue) => {
        if (!dateValue) return 'N/A';

        try {
            const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
            return isNaN(date.getTime()) ? 'N/A' : format(date, 'MM-dd-yyyy HH:mm:ss');
        } catch {
            return 'N/A';
        }
    }, []);

    const Header = ({ vehData, title }) => {
        if (!vehData) {
            return (
                <Box sx={{ 
                    mb: 2,
                    p: 2,
                    border: '1px solid', borderColor: 'divider'
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        No Vehicle Data Found
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ 
                mb: 2,
                p: 3,
                border: '1px solid', borderColor: 'divider'
            }}>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2 }}>
                    {title}
                </Typography>
                <Box sx={{ 
                    display: 'flex', 
                    gap: 3, 
                    flexWrap: 'wrap'
                }}>
                    {[
                        { label: 'Id', value: vehData.id },
                        { label: 'VIN', value: vehData.vin },
                        { label: 'vc_Rel_Name', value: vehData.vc_rel_name },
                        { label: 'Percentage Match', value: vehData.percentage_match },
                        { label: 'As Of Date', value: vehData.as_of_dt }
                    ].map(({ label, value }) => (
                        <Typography key={label} variant="body2">
                            <strong>{label}:</strong> {value}
                        </Typography>
                    ))}
                </Box>
            </Box>
        );
    };

    const displayEcuInfo = ({ ecuData, title2 }) => {
        if (!ecuData) {
            return (
                <Box sx={{ 
                    mb: 2,
                    p: 2,
                    border: '1px solid', borderColor: 'divider'
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        No ECU Data Selected Or Found
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ 
                mb: 2,
                p: 3,
                border: '1px solid', borderColor: 'divider'
            }}>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2 }}>
                    {title2}
                </Typography>
                <Box sx={{ 
                    display: 'flex', 
                    gap: 3, 
                    flexWrap: 'wrap'
                }}>
                    {[
                        { label: 'ECU Name', value: ecuData.ecuName },
                        { label: 'Valid From Date', value: ecuData.validFromDate },
                        { label: 'Latest Vescom', value: ecuData.latestVescom },
                        { label: 'Earliest Vescom', value: ecuData.earliestVescom },
                        { label: 'Mismatched PNs', value: ecuData.mismatchedPNs },
                        { label: 'Software Version', value: ecuData.softwareVersion },
                        { label: 'Percentage Match', value: ecuData.percentageMatch },
                        { label: 'Source', value: ecuData.source },
                        { label: 'Diagnostic Id', value: ecuData.diagnosticId }
                    ].map(({ label, value }) => (
                        <Typography key={label} variant="body2">
                            <strong>{label}:</strong> {value}
                        </Typography>
                    ))}
                </Box>
            </Box>
        );
    };

    const renderExpandableCell = useCallback((content, rowId, columnKey, maxLength = 50) => {
        if (!content || content === 'N/A') return content;

        const cellId = `${rowId}-${columnKey}`;
        const isExpanded = expandedCells[cellId];
        const shouldTruncate = String(content).length > maxLength;
        const isCopied = copiedCell === cellId;

        if (!shouldTruncate) {
            return (
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: columnKey.includes('code') || columnKey.includes('number') 
                            ? 'monospace' 
                            : 'inherit',
                        fontSize: '12px',
                        wordBreak: 'break-word'
                    }}
                >
                    {content}
                </Typography>
            );
        }

        return (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%' }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: columnKey.includes('code') || columnKey.includes('number') 
                            ? 'monospace' 
                            : 'inherit',
                        fontSize: '12px',
                        wordBreak: 'break-word',
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: isExpanded ? 'unset' : 'ellipsis',
                        whiteSpace: isExpanded ? 'normal' : 'nowrap',
                        maxWidth: isExpanded ? 'none' : '200px'
                    }}
                >
                    {isExpanded ? content : `${String(content).substring(0, maxLength)}...`}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.25 }}>
                    <Tooltip title={isCopied ? 'Copied!' : 'Copy to clipboard'}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(content, cellId);
                            }}
                            sx={{
                                p: 0.25,
                                minWidth: 'auto',
                                color: isCopied ? 'success.main' : 'text.secondary'
                            }}
                        >
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={isExpanded ? 'Collapse' : 'Expand full text'}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleCellExpansion(cellId);
                            }}
                            sx={{ p: 0.25, minWidth: 'auto' }}
                        >
                            {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
        );
    }, [expandedCells, toggleCellExpansion, copyToClipboard, copiedCell]);

    const fetchPNPFile = useCallback(async () => {
        await getPNPFromStorage();
    }, []);

    const processVehicleData = useCallback((data) => {
        const vehicleData = data?.vehicle_data;
        if (!vehicleData) return [];

        const vehicleDataObj = {
            id: vehicleData.id || 'N/A',
            vin: vehicleData.vin || 'N/A',
            vc_rel_name: vehicleData.vc_rel_name || 'N/A',
            percentage_match: vehicleData.percentage_match || 'N/A',
            as_of_dt: formatDateSafely(vehicleData.as_of_dt),
            created_at: formatDateSafely(vehicleData.created_at),
            updated_at: formatDateSafely(vehicleData.updated_at),
            ecu_infos: vehicleData.ecu_infos || []
        };

        setVehData(vehicleDataObj);

        return vehicleDataObj.ecu_infos.map(value => ({
            ecuName: value.ecu_name || 'N/A',
            validFromDate: formatDateSafely(value.valid_from_dt),
            latestVescom: value.latest_vescom || 'N/A',
            earliestVescom: value.earliest_vescom || 'N/A',
            mismatchedPNs: value.mismatched_pns || 'N/A',
            softwareVersion: value.software_version || 'N/A',
            percentageMatch: value.percentage_match || 'N/A',
            source: value.source || 'N/A',
            diagnosticId: value.diagnostic_id || 'N/A'
        }));
    }, [formatDateSafely]);

    const convertPNPDataToTable = useCallback((data) => {
        if (!data?.data) {
            return [{
                diagnostic_id: 'raw_data',
                did: 'Raw Data',
                response: typeof data === 'string' ? data : JSON.stringify(data, null, 2),
                module: 'N/A',
                vin: 'N/A',
                date: 'See description for raw data',
                filename: 'N/A'
            }];
        }

        setPNPDate(data.data[0]?.date || 'N/A');

        return data.data.map(value => ({
            diagnostic_id: value.diagnostic_id || 'N/A',
            did: value.did || 'N/A',
            response: value.response || 'N/A',
            module: value.module || 'N/A',
            vin: value.vin || 'N/A',
            date: value.date || 'N/A',
            filename: value.filename || 'N/A'
        }));
    }, []);

    const columns = useMemo(() => [
        {
            accessorKey: 'diagnostic_id',
            header: 'Diagnostic ID',
            enableColumnFilter: true,
            filterFn: 'includesString',
            Cell: ({ cell }) => (
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        fontWeight: 'bold'
                    }}
                >
                    {cell.getValue()}
                </Typography>
            ),
            Filter: ({ column }) => (
                <TextField
                    size="small"
                    placeholder="Search diagnostic ID..."
                    value={column.getFilterValue() ?? ''}
                    onChange={(e) => column.setFilterValue(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: column.getFilterValue() && (
                                <InputAdornment position="end">
                                    <Clear
                                        fontSize="small"
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => column.setFilterValue('')}
                                    />
                                </InputAdornment>
                            )
                        }
                    }}
                    sx={{ width: '100%' }}
                />
            )
        },
        {
            accessorKey: 'did',
            header: 'DID',
            enableColumnFilter: true,
            filterFn: 'includesString',
            Cell: ({ cell }) => (
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        fontWeight: 'bold'
                    }}
                >
                    {cell.getValue()}
                </Typography>
            ),
            Filter: ({ column }) => (
                <TextField
                    size="small"
                    placeholder="Search DID..."
                    value={column.getFilterValue() ?? ''}
                    onChange={(e) => column.setFilterValue(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: column.getFilterValue() && (
                                <InputAdornment position="end">
                                    <Clear
                                        fontSize="small"
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => column.setFilterValue('')}
                                    />
                                </InputAdornment>
                            )
                        }
                    }}
                    sx={{ width: '100%' }}
                />
            )
        },
        {
            accessorKey: 'module',
            header: 'Module',
            enableColumnFilter: true,
            filterFn: 'includesString',
            Cell: ({ cell, row }) => renderExpandableCell(cell.getValue(), row.id, 'manufacturer', 30),
            Filter: ({ column }) => (
                <TextField
                    size="small"
                    placeholder="Search module..."
                    value={column.getFilterValue() ?? ''}
                    onChange={(e) => column.setFilterValue(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: column.getFilterValue() && (
                                <InputAdornment position="end">
                                    <Clear
                                        fontSize="small"
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => column.setFilterValue('')}
                                    />
                                </InputAdornment>
                            )
                        }
                    }}
                    sx={{ width: '100%' }}
                />
            )
        },
        {
            accessorKey: 'response',
            header: 'Response',
            enableColumnFilter: true,
            filterFn: 'includesString',
            Cell: ({ cell, row }) => renderExpandableCell(cell.getValue(), row.id, 'response', 80),
            Filter: ({ column }) => (
                <TextField
                    size="small"
                    placeholder="Search response..."
                    value={column.getFilterValue() ?? ''}
                    onChange={(e) => column.setFilterValue(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: column.getFilterValue() && (
                                <InputAdornment position="end">
                                    <Clear
                                        fontSize="small"
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => column.setFilterValue('')}
                                    />
                                </InputAdornment>
                            )
                        }
                    }}
                    sx={{ width: '100%' }}
                />
            )
        }
    ], [renderExpandableCell]);

    const table = useMaterialReactTable({
        columns,
        data: pnpData,
        enableGlobalFilter: false,
        enableColumnFilters: true,
        onColumnFiltersChange: setColumnFilters,
        enableRowSelection: true,
        onRowSelectionChange: setRowSelection,
        enableMultiRowSelection: false,
        state: { columnFilters, rowSelection },
        columnFilterDisplayMode: 'subheader',
        initialState: {
            density: 'compact',
            showColumnFilters: true
        },
        enablePagination: false,
        muiTableBodyRowProps: ({ row, index }) => ({
            sx: {
                backgroundColor: index % 2 === 0 ? 'inherit' : 'rgba(0, 0, 0, 0.02)',
                '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)'
                },
                height: Object.keys(expandedCells)
                    .some(key => key.startsWith(row.id) && expandedCells[key]) 
                    ? 'auto' 
                    : '52px',
                minHeight: '52px'
            }
        }),
        muiTableProps: {
            sx: {
                '& .MuiTableCell-root': {
                    padding: '8px',
                    borderBottom: '1px solid',
                    borderBottomColor: 'divider',
                    verticalAlign: 'top'
                },
                '& .MuiTableHead-root .MuiTableCell-root': {
                    backgroundColor: 'var(--mui-palette-background-default)',
                    fontWeight: 'bold'
                }
            }
        },
        renderTopToolbarCustomActions: () => (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="subtitle2">
                    PNP Date ({pnpDate})
                </Typography>
            </Box>
        ),
        enableColumnActions: true,
        enableSorting: true,
        enableTopToolbar: true,
        enableBottomToolbar: false
    });

    useEffect(() => {
        fetchPNPFile();
    }, [fetchPNPFile]);

    useEffect(() => {
        const selectedRows = table.getSelectedRowModel().rows;
        if (selectedRows.length > 0) {
            const diagId = selectedRows[0].original.diagnostic_id;
            const ecuMatch = ecuData.find(ecu => ecu.diagnosticId === diagId);
            setSelectedEcuData(ecuMatch || null);
        } else {
            setSelectedEcuData(null);
        }
    }, [table.getSelectedRowModel().rows, ecuData]);

    const handleRefresh = () => {
        fetchPNPFile();
        setColumnFilters([]);
        setExpandedCells({});
    };

    if (loading) {
        return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <CircularProgress size={48} sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ mt: 2 }}>
                    Loading PNP file...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="body1" sx={{ mb: 3 }}>
                    {error}
                </Typography>
                <Button
                    variant="outlined"
                    onClick={handleRefresh}
                    startIcon={<Refresh />}
                    sx={{ 
                        textTransform: 'none',
                        borderColor: 'divider'
                    }}
                >
                    Retry
                </Button>
            </Box>
        );
    }

    if (!rawData) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                    No PNP data available
                </Typography>
                <Button
                    variant="outlined"
                    onClick={handleRefresh}
                    startIcon={<Refresh />}
                    sx={{ 
                        textTransform: 'none',
                        borderColor: 'divider'
                    }}
                >
                    Load PNP File
                </Button>
            </Box>
        );
    }

    return (
        <>
            <Header title={title} vehData={vehData} />
            {displayEcuInfo({ ecuData: selectedEcuData, title2 })}
            <Box sx={{ mt: 2 }}>
                {viewMode === 'table' ? (
                    <Box sx={{ 
                        maxHeight: 700, 
                        overflow: 'auto', 
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'background.paper'
                    }}>
                        <MaterialReactTable table={table} />
                    </Box>
                ) : (
                    <Box sx={{ 
                        border: '1px solid',
                        borderColor: 'divider', 
                        p: 3, 
                        maxHeight: 600, 
                        overflow: 'auto'
                    }}>
                        <pre style={{ 
                            whiteSpace: 'pre-wrap', 
                            wordBreak: 'break-word', 
                            fontFamily: 'monospace', 
                            fontSize: '13px', 
                            lineHeight: 1.6, 
                            margin: 0
                        }}>
                            {typeof rawData === 'string' ? rawData : JSON.stringify(rawData, null, 2)}
                        </pre>
                    </Box>
                )}
            </Box>
        </>
    );
};

export default PNPComponent;